import { ArrowLeft, Heart, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import donation1 from "../assets/donation-1.jpg";

const DonationDetail = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("50");

  const campaign = {
    title: "Organization Garden Project",
    description:
      "Help us build a sustainable organization garden for local families",
    raised: 12500,
    goal: 20000,
    donors: 87,
  };

  const progress = (campaign.raised / campaign.goal) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/donations")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-primary">
                Campaign Details
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-64 overflow-hidden md:h-80">
        <img
          src={donation1}
          alt={campaign.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50" />
      </div>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            <div>
              <h1 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">
                {campaign.title}
              </h1>
              <p className="text-lg text-muted-foreground">
                {campaign.description}
              </p>
            </div>

            {/* Progress Card */}
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div>
                  <div className="mb-2 flex justify-between">
                    <span className="text-2xl font-bold text-primary">
                      ${campaign.raised.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">
                      raised of ${campaign.goal.toLocaleString()} goal
                    </span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </div>
                <div className="flex items-center justify-between border-t pt-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Heart className="h-4 w-4" />
                    <span>{campaign.donors} donors</span>
                  </div>
                  <div className="flex items-center gap-1 font-medium text-primary">
                    <TrendingUp className="h-4 w-4" />
                    <span>{Math.round(progress)}% funded</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* About Campaign */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">
                About This Campaign
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                We&apos;re excited to announce our Organization Garden Project!
                This initiative will create a beautiful, sustainable garden
                space where local families can grow fresh produce, learn about
                organic farming, and connect with their neighbors.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                Your donation will help us purchase seeds, tools, soil, and
                build raised garden beds. Together, we can create a green oasis
                that brings our organization closer while promoting healthy,
                sustainable living.
              </p>
            </div>
          </div>

          {/* Sidebar - Donation Widget */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Make a Donation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  {["25", "50", "100", "250"].map(preset => (
                    <Button
                      key={preset}
                      variant={amount === preset ? "default" : "outline"}
                      onClick={() => setAmount(preset)}
                      className="w-full"
                    >
                      ${preset}
                    </Button>
                  ))}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Custom Amount
                  </label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="text-lg"
                  />
                </div>
                <Button className="w-full" size="lg">
                  Donate ${amount}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DonationDetail;
